/**
 * Dev-only decoration of resolved terms, so a word that was NEVER tagged becomes visible.
 *
 * The `⟪MISSING TERM: key.variant⟫` sentinel in `term-resolution.ts` catches a tag that points at
 * nothing. That is the rare failure. The common one is the opposite: a domain word typed straight
 * into a template instead of being interpolated from the glossary. It produces no lookup, so there
 * is no event to detect and no marker to render - an absence has no signal of its own.
 *
 * Decorating the SUCCESSES is the only way to make that absence visible. With this on, every word
 * the glossary produced is bracketed, so any undecorated domain word left on the screen is a
 * suspect the eye can find without knowing in advance where to look.
 *
 * This is a dev affordance, not a guard: it reveals a miss on a screen someone opens and looks at,
 * and the finding is discarded when the tab closes. Turning the residue into a CI assertion was
 * considered and deliberately deferred by the architect on 2026-08-17; the tracked entry is in this
 * branch's plan file.
 */

/**
 * Brackets a decorated term.
 *
 * NOT the `<*...*>` of the original sketch: `<` escapes to `&lt;` in text content and can be parsed
 * or swallowed in an attribute position, which would leave the marker in two different forms
 * depending on where it landed and make any later grep unreliable.
 *
 * `⟦ ⟧` is deliberately a DIFFERENT pair from the `⟪ ⟫` of the missing-term marker, so the two
 * cannot be confused in a screenshot, a video or a grep: one means "this word came from the
 * glossary", the other means "this tag resolved to nothing". Neither pair occurs in product copy.
 */
export const TERM_DEBUG_OPEN = '⟦';
export const TERM_DEBUG_CLOSE = '⟧';

/**
 * Vite injects env via `import.meta.env`; cast only that piece locally.
 *
 * The same convention as `router.ts:61`, and it is load-bearing rather than stylistic here. This
 * module is reached transitively by `tsconfig.e2e.json`, which type-checks the Playwright suite
 * with Node types and deliberately no Vite client types, so a bare `import.meta.env` fails that
 * compilation with TS2339. `env` is optional in the cast because a pure Node context has no Vite
 * injection at all, and reading `.DEV` off an undefined would throw during module load.
 */
const viteEnv = (import.meta as unknown as { env?: { DEV?: boolean } }).env;

/** The localStorage key holding the opt-in, so it survives SPA navigation and a reload. */
const TERM_DEBUG_STORAGE_KEY = 'agwa.termDebug';

/** The URL parameter that sets or clears the opt-in: `?termdebug=1` on, `?termdebug=0` off. */
const TERM_DEBUG_URL_PARAM = 'termdebug';

/**
 * Whether decoration is on, resolved ONCE at module load.
 *
 * Read eagerly rather than per access because `terms` is built at import time: every form of every
 * term is derived once by `resolveTerm`, so a later toggle could not reach values that already
 * exist. Changing the setting therefore requires a reload, which is also the honest behaviour -
 * half a page decorated and half not would be worse than either state.
 */
let enabled = false;

/**
 * Resolves the opt-in from the URL first, then from storage.
 *
 * Gated on the Vite DEV flag before anything else so this CANNOT be reached in a production
 * build: a real user seeing `⟦company⟧` would be the exact facade failure this repository forbids,
 * running backwards. The URL wins over storage so a decorated tab can always be cleared with
 * `?termdebug=0` without opening devtools, and the result is written back to storage so the setting
 * survives the client-side navigation that drops the query string.
 *
 * Every storage access is wrapped: `localStorage` throws rather than returning null in a private
 * window and under some embedded webviews, and a debugging aid must never be the thing that stops
 * the application booting.
 */
function resolveEnabled(): boolean {
  if (!viteEnv?.DEV) return false;
  if (typeof window === 'undefined') return false;

  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(TERM_DEBUG_STORAGE_KEY);
  } catch {
    stored = null;
  }

  const requested = new URLSearchParams(window.location.search).get(
    TERM_DEBUG_URL_PARAM
  );

  if (requested === null) return stored === '1';

  const on = requested === '1' || requested === 'true';
  try {
    window.localStorage.setItem(TERM_DEBUG_STORAGE_KEY, on ? '1' : '0');
  } catch {
    // A browser that refuses storage still honours the URL for this page load.
  }
  return on;
}

enabled = resolveEnabled();

/** True when resolved terms are being decorated. Exported so a test can assert the default is off. */
export function termDebugEnabled(): boolean {
  return enabled;
}

/**
 * Wraps one resolved form, or returns it untouched when the affordance is off.
 *
 * Applied to every derived form rather than only to `value`, because a missed word is just as
 * likely to be a plural or a possessive, and those are the forms a hand-typed string tends to get
 * wrong in a way that reads fine.
 */
export function decorateTerm(form: string): string {
  return enabled ? `${TERM_DEBUG_OPEN}${form}${TERM_DEBUG_CLOSE}` : form;
}

/**
 * Strips decoration from a string.
 *
 * Exists so that anything comparing or matching on a term's text keeps working while the affordance
 * is on. Without it, turning decoration on would change behaviour rather than only appearance, and
 * a debugging aid that alters what it observes is worse than none.
 */
export function undecorateTerms(text: string): string {
  return text.split(TERM_DEBUG_OPEN).join('').split(TERM_DEBUG_CLOSE).join('');
}

/** For tests that need to exercise both states without a reload. */
export function _setTermDebugForTests(value: boolean): void {
  enabled = value;
}
